// Carrier statement parser — extracted from CommissionTracker.jsx
// so it can be exercised from Node tests against the seeded fixtures
// per seeder brief Phase 1d.
//
// Prior to this extraction the XLSX → transactions transform lived
// inline in a React onClick handler and could not be imported without
// dragging the whole app graph. That meant the commission-math tests
// were asserting fixture-side pure arithmetic against expected.json —
// the ACTUAL production parser was untested end-to-end.
//
// Public surface:
//   CHARGEBACK_TYPES              — Set<string>. Activity type labels
//                                   that count as chargebacks.
//   parseWorkbook(wb, opts)       — Given an XLSX workbook object,
//                                   returns { transactions, producers }.
//   parseXlsxBuffer(buf, opts)    — Convenience: takes an ArrayBuffer
//                                   or Node Buffer, dynamically imports
//                                   xlsx, and delegates to parseWorkbook.
//   aggregateFromTransactions(txs)— Per-producer + org totals in cents.
//
// This module is CommonJS-safe (no `import` at top level, dynamic
// require of xlsx) so it works from both CRA (which uses Babel) and
// node --test.

'use strict';

const CHARGEBACK_TYPES = new Set([
  // MOO statement activity types
  'LAPSE', 'LAPSE REV ADV', 'RECPT ADV CO', 'BFY REVERSAL', 'CANC EFF ISS', 'NEW ISS REV',
  // Americo
  'CHARGEBACK - LAPSE', 'CHARGEBACK - SURRENDER', 'CHARGEBACK - DEATH',
  'CHARGEBACK - ADVANCE LAPSE', 'CHARGEBACK - ADVANCE REISSUE',
  // Transamerica / generic
  'Chargeback', 'CHARGEBACK', 'chargeback',
]);

// The MoO DETAIL sheet columns. Kept in one place so the seed
// fixture and the production parser agree on names/casing.
const COL_ALIASES = {
  prodNum:      ['Prod Num', 'producer', 'agent id'],
  company:      ['Company', 'carrier'],
  mga:          ['MGA Name'],
  policy:       ['Policy', 'policy number'],
  insured:      ['Insureds Name', 'insured', 'insured name'],
  state:        ['Issue State', 'state'],
  activityDate: ['Activity Date'],
  issueDate:    ['Issue Date'],
  planCode:     ['Plan Code'],
  commPremium:  ['Comm Premium', 'commission premium'],
  activityType: ['Activity Type', 'activity_type'],
  commPct:      ['Comm Pct', 'commission pct'],
  commAmt:      ['Comm Amt', 'commission', 'commission amount'],
  advComm:      ['Adv Comm', 'advance commission'],
};

// Case + underscore tolerant key lookup on a row object.
function pickCell(row, aliases) {
  const keys = Object.keys(row);
  for (const alias of aliases) {
    const norm = String(alias).toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    const found = keys.find(k =>
      String(k).toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim() === norm
    );
    if (found !== undefined) return row[found];
  }
  return null;
}

/**
 * Turn a raw XLSX row (a plain object keyed by column name) into a
 * normalized transaction. Pure function. `sourceName` is the file
 * name — used for id + provenance.
 */
function rowToTransaction(row, idx, sourceName) {
  const prodNum = String(pickCell(row, COL_ALIASES.prodNum) || '').trim();
  if (!prodNum) return null;                    // producer-less rows are skipped by prod parser

  const activityType = pickCell(row, COL_ALIASES.activityType);
  const commAmt = Number(pickCell(row, COL_ALIASES.commAmt)) || 0;
  const advComm = Number(pickCell(row, COL_ALIASES.advComm)) || 0;
  const netToAccount = commAmt;

  const activityDate = pickCell(row, COL_ALIASES.activityDate) || '';
  const policy       = pickCell(row, COL_ALIASES.policy) || '';

  return {
    id: `${sourceName}|${prodNum}|${policy}|${activityDate}|${activityType || ''}|${idx}`,
    company:      pickCell(row, COL_ALIASES.company),
    mga:          pickCell(row, COL_ALIASES.mga),
    prodNum,
    policy,
    insured:      pickCell(row, COL_ALIASES.insured),
    state:        pickCell(row, COL_ALIASES.state),
    activityDate,
    issueDate:    pickCell(row, COL_ALIASES.issueDate),
    planCode:     String(pickCell(row, COL_ALIASES.planCode) || '').trim(),
    commPremium:  Number(pickCell(row, COL_ALIASES.commPremium)) || 0,
    activityType,
    commPct:      Number(pickCell(row, COL_ALIASES.commPct)) || 0,
    commAmt,
    advComm,
    netToAccount,
    source: sourceName,
    // A row is a chargeback if its activity type is in the known
    // chargeback set OR the net commission went negative.
    isChargeback: CHARGEBACK_TYPES.has(activityType) || netToAccount < 0,
  };
}

/**
 * Given an xlsx Workbook object, extract transactions from the
 * DETAIL sheet (or the first sheet if no DETAIL is found).
 *
 * @param {object} wb                       xlsx workbook
 * @param {object} XLSX                     the xlsx module (passed so
 *                                          we don't require twice)
 * @param {string} [opts.sourceName='seed'] file name for tx.id + provenance
 * @returns {{ transactions: Array, producers: Object }}
 */
function parseWorkbook(wb, XLSX, { sourceName = 'seed' } = {}) {
  if (!wb || !wb.SheetNames || wb.SheetNames.length === 0) {
    return { transactions: [], producers: {} };
  }
  const sheetName = wb.SheetNames.find(n => String(n).toUpperCase().includes('DETAIL')) || wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: null });

  const transactions = [];
  const producers = {};

  rows.forEach((r, idx) => {
    const tx = rowToTransaction(r, idx, sourceName);
    if (!tx) return;
    transactions.push(tx);
    if (!producers[tx.prodNum]) {
      producers[tx.prodNum] = { team: 'unassigned', name: '' };
    }
  });

  return { transactions, producers };
}

/**
 * Convenience: take an ArrayBuffer / Node Buffer, dynamically require
 * xlsx, and return the same shape as parseWorkbook.
 */
async function parseXlsxBuffer(buf, { sourceName = 'seed' } = {}) {
  // Dynamic import so this module is cheap to load in non-XLSX code
  // paths. eslint-disable-next-line global-require
  const XLSX = require('xlsx');
  const wb = XLSX.read(buf, { type: 'array' });
  return parseWorkbook(wb, XLSX, { sourceName });
}

/**
 * Per-producer + org aggregates in cents. Used by tests to assert
 * app output === expected.json.
 *
 * Chargebacks are detected the same way rowToTransaction does it:
 * either the tx.isChargeback flag is true, OR commAmt is negative.
 * We convert dollars to cents with Math.round to avoid float drift.
 */
function aggregateFromTransactions(txs) {
  const perProducer = {};
  let totalGrossCents = 0;
  let totalChargebackCents = 0;
  for (const tx of txs) {
    const cents = Math.round((tx.commAmt || 0) * 100);
    const p = tx.prodNum;
    if (!perProducer[p]) perProducer[p] = { gross_cents: 0, chargeback_cents: 0, net_cents: 0 };
    if (tx.isChargeback || cents < 0) {
      const abs = Math.abs(cents);
      perProducer[p].chargeback_cents += abs;
      totalChargebackCents += abs;
    } else {
      perProducer[p].gross_cents += cents;
      totalGrossCents += cents;
    }
    perProducer[p].net_cents = perProducer[p].gross_cents - perProducer[p].chargeback_cents;
  }
  return {
    per_producer: perProducer,
    totals: {
      gross_cents:      totalGrossCents,
      chargeback_cents: totalChargebackCents,
      net_cents:        totalGrossCents - totalChargebackCents,
    },
  };
}

module.exports = {
  CHARGEBACK_TYPES,
  COL_ALIASES,
  pickCell,
  rowToTransaction,
  parseWorkbook,
  parseXlsxBuffer,
  aggregateFromTransactions,
};
